import { Box, Container } from "@material-ui/core";
import { graphql } from "gatsby";
import React from "react";
import ContentModules from "../../content-modules";
import Seo from "../seo";
import PageDocument from "./PageDocument";
import Header, { PageHeaderProps } from "./PageHeader";
import {
  WebPageFragment_descriptionNode,
  WebPageFragment_documentNode,
  WebPageFragment_modules,
  WebPageFragment_seoMetaTags
} from "./__generated__/WebPageFragment";
import get from "lodash/get";
import theme from "../../theme/material-ui-theme";

type PageProps = {
  slug?: string;
  title?: string;
  descriptionNode?: WebPageFragment_descriptionNode;
  documentNode?: WebPageFragment_documentNode;
  modules?: WebPageFragment_modules[];
  seoMetaTags?: WebPageFragment_seoMetaTags;
  className?: string;
  container?: "fluid" | "xs" | "sm" | "md" | "lg" | "xl";
  background?: string;
  HeaderProps?: PageHeaderProps;
  children?: React.ReactNode;
  containerDisableGutters?: boolean | null;
};

/**
 * DatoCMS WebPage
 */
export default function WebPage({
  seoMetaTags,
  title,
  descriptionNode,
  documentNode,
  HeaderProps,
  modules,
  children,
  container,
  containerDisableGutters,
  background,
  className
}: PageProps) {
  const description =
    descriptionNode && descriptionNode?.childMarkdownRemark.html;
  const document = documentNode && documentNode?.childMarkdownRemark.html;

  const containerStyles: React.CSSProperties = {};

  // Can be hex code or palette key: "primary.main"
  if (background) {
    containerStyles.backgroundColor = background.startsWith("#")
      ? background
      : get(theme.palette, background);
  }

  return (
    <Box className={`page-wrapper ${className || ""}`} style={containerStyles}>
      {seoMetaTags && <Seo seoMetaTags={seoMetaTags} />}

      {/* Custom pages can specify a children component */}
      {children}

      {/* Page title (Only used by static documents) */}
      {title && (
        <Header
          title={title}
          description={description}
          {...(HeaderProps || {})}
        />
      )}

      {/* Page content modules */}
      {!document && (
        <>
          {modules && modules.length > 0 && (
            <ContentModules
              contentModules={modules}
              pageContainer={container}
              pageContainerDisableGutters={containerDisableGutters}
            />
          )}
        </>
      )}

      {/* Documents are for text only pages like TOS/Privacy policy, etc */}
      {/* It should be refactored into a content module */}
      {document && (
        <Container maxWidth={container === "fluid" ? false : container}>
          <PageDocument html={document} />
        </Container>
      )}
    </Box>
  );
}

export const WebPageFragment = graphql`
  fragment WebPageFragment on DatoCmsWebPage {
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    path
    title
    descriptionNode {
      childMarkdownRemark {
        html
      }
    }
    documentNode {
      childMarkdownRemark {
        html
      }
    }
    container
    containerDisableGutters
    background
    ...WebPageContentModulesFragment
  }
`;
